import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useBooking } from "../context/BookingContext";
import { OrangeButton } from "./blocks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Clarity Session", "Decoding Intuition", "Accelerate", "Black Swan Assessment", "Leadership", "Sales", "Public Speaking", "IBN Network", "Free Assessment", "Contact"];

const inputCls =
  "w-full bg-callout text-cream placeholder-cream/35 rounded-xl px-4 py-3.5 text-[15px] outline-none focus:ring-1 focus:ring-ember transition";

export default function BookingModal() {
  const { open, setOpen, interest, setInterest } = useBooking();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest, source: "booking-modal" });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    setOpen(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ name: "", email: "", company: "", message: "" }); }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-surface border-0 text-cream rounded-2xl max-w-md p-0 overflow-hidden shadow-[0_40px_90px_rgba(0,0,0,0.6)]">
        {done ? (
          <div className="p-10 text-center" data-testid="booking-success">
            <CheckCircle2 className="w-14 h-14 text-ember mx-auto" />
            <h3 className="font-heading text-2xl mt-5">Request received.</h3>
            <p className="text-cream/60 mt-3">Maurice's team will reach out within 1 business day to confirm your session.</p>
            <OrangeButton onClick={() => close(false)} className="mt-7" testid="booking-done-close">CLOSE</OrangeButton>
          </div>
        ) : (
          <>
            <DialogHeader className="px-7 pt-7">
              <DialogTitle className="font-heading text-2xl text-cream text-left">Book Your Session</DialogTitle>
              <DialogDescription className="text-cream/55 text-sm text-left">Tell us a little about you and we'll be in touch.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="px-7 pb-7 pt-5 space-y-3">
              <input data-testid="booking-name" className={inputCls} placeholder="Full name" value={form.name} onChange={upd("name")} />
              <input data-testid="booking-email" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={upd("email")} />
              <input data-testid="booking-company" className={inputCls} placeholder="Company (optional)" value={form.company} onChange={upd("company")} />
              <select data-testid="booking-interest" className={inputCls} value={interest} onChange={(e) => setInterest(e.target.value)}>
                {INTERESTS.map((i) => <option key={i} value={i} className="bg-callout">{i}</option>)}
              </select>
              <textarea data-testid="booking-message" rows={3} className={inputCls} placeholder="What would you like to work on? (optional)" value={form.message} onChange={upd("message")} />
              <OrangeButton onClick={submit} testid="booking-submit" className="w-full mt-2" type="submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "REQUEST MY SESSION"}
              </OrangeButton>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
